import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

// Google Maps API is now loaded via unified system in index.html
// This provider just manages the loading state and creates utilities

// Create context for Google Maps availability
const MapsContext = createContext({
  isLoaded: false,
  loadError: null,
  createMarkerIcon: () => null,
  mapStyles: [],
  mapOptions: {},
  defaultLocation: {
    name: "Lasmari Apraos",
    lat: 39.796276,
    lng: 19.884997
  }
});

export const useMaps = () => useContext(MapsContext);

// Light map style, warm cream land with soft Aegean water (red & white vibe)
const modernMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#f4eeeb" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#5b4d4f" }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#e3d8d3" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#1a1012" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8a7a7c" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#d9e6cf" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6f8a5e" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#ece2dd" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7a6c6e" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f6dcd8" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#eebab2" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#a85a52" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#ece2dd" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8a7a7c" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#bfe0e4" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7fa9ad" }],
  },
];

// Standard map UI options
const standardMapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  styles: modernMapStyles
};

// SVG path for a modern pin marker
const pinSvgPath = "M12 0C7.31 0 3.5 3.81 3.5 8.5c0 4.77 6.96 11.11 8.5 12.5 1.54-1.39 8.5-7.73 8.5-12.5 0-4.69-3.81-8.5-8.5-8.5zm0 11.5c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3z";

/**
 * MapsProvider - Simple provider using unified loading from index.html
 */
export const MapsProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [defaultLocation, setDefaultLocation] = useState({
    name: "Lasmari Apraos",
    lat: 39.796276,
    lng: 19.884997
  });
  
  // Fetch default location from settings
  useEffect(() => {
    const fetchDefaultLocation = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (response.data && response.data.defaultLocation) {
          console.log('Using default location from settings:', response.data.defaultLocation);
          setDefaultLocation(response.data.defaultLocation);
        }
      } catch (error) {
        console.error('Error fetching default location from settings:', error);
      }
    };
    
    fetchDefaultLocation();
  }, []);
  
  // Create marker icon function
  const createMarkerIcon = useCallback((type = 'default', options = {}) => {
    const googleMaps = window.google?.maps;
    const defaultScale = 1.8;
    
    let fillColor, strokeColor, scale;
    
    switch(type) {
      case 'pickup':
        fillColor = '#ffffff';
        strokeColor = '#000000';
        scale = defaultScale;
        break;
      case 'dropoff':
        fillColor = '#22c55e';
        strokeColor = '#000000';
        scale = defaultScale;
        break;
      case 'circle':
        fillColor = options.color || '#ffffff';
        strokeColor = options.strokeColor || '#000000';
        scale = options.scale || 10;
        break;
      default:
        fillColor = options.color || '#ffffff';
        strokeColor = options.strokeColor || '#000000';
        scale = options.scale || defaultScale;
    }
    
    fillColor = options.color || fillColor;
    strokeColor = options.strokeColor || strokeColor;
    scale = options.scale || scale;
    
    if (googleMaps && googleMaps.SymbolPath) {
      if (type === 'circle') {
        return {
          path: googleMaps.SymbolPath.CIRCLE,
          fillColor: fillColor,
          fillOpacity: 1,
          strokeColor: strokeColor,
          strokeWeight: 2,
          scale: scale
        };
      }
      
      return {
        path: pinSvgPath,
        fillColor: fillColor,
        fillOpacity: 1,
        strokeColor: strokeColor,
        strokeWeight: 1.5,
        scale: scale,
        anchor: new googleMaps.Point(12, 21),
        labelOrigin: new googleMaps.Point(12, 9)
      };
    }
    
    // Fallback when Google Maps isn't loaded yet
    if (type === 'circle') {
      return {
        path: 1,
        fillColor: fillColor,
        fillOpacity: 1,
        strokeColor: strokeColor,
        strokeWeight: 2,
        scale: scale
      };
    }
    
    return {
      path: pinSvgPath,
      fillColor: fillColor,
      fillOpacity: 1,
      strokeColor: strokeColor,
      strokeWeight: 1.5,
      scale: scale,
      anchor: { x: 12, y: 21 },
      labelOrigin: { x: 12, y: 9 }
    };
  }, []);
  
  // Simple loading detection using the unified system
  useEffect(() => {
    console.log("MapsProvider: Checking for Google Maps...");
    
    // Register callback with unified loading system
    if (window.registerMapsCallback) {
      window.registerMapsCallback(() => {
        console.log("MapsProvider: Maps loaded via unified system");
        setIsLoaded(true);
      });
    } else {
      // Fallback polling if unified system isn't ready yet
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          console.log("MapsProvider: Maps loaded via polling");
          setIsLoaded(true);
          clearInterval(checkInterval);
        }
        
        if (window._mapsLoaded) {
          console.log("MapsProvider: Maps loaded via flag");
          setIsLoaded(true);
          clearInterval(checkInterval);
        }
      }, 100);
      
      // Safety timeout
      const timeout = setTimeout(() => {
        console.log("MapsProvider: Timeout - forcing loaded state");
        setIsLoaded(true);
        clearInterval(checkInterval);
      }, 3000);
      
      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }
  }, []);
  
  // Provide context to children
  return (
    <MapsContext.Provider 
      value={{
        isLoaded,
        loadError,
        google: isLoaded ? window.google : null,
        createMarkerIcon,
        mapStyles: modernMapStyles,
        mapOptions: standardMapOptions,
        defaultLocation
      }}
    >
      {children}
      
      {/* Add CSS to ensure map elements are visible */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Force map elements to be visible */
          .gm-style img, .gm-style-cc, .gmnoprint {
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Force directions path to be visible */
          .gm-style > div > div:nth-child(4) > div {
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Add animation for marker bounce effect */
          @keyframes markerPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          .marker-animation {
            animation: markerPulse 1.5s infinite ease-in-out;
          }
        `
      }} />
    </MapsContext.Provider>
  );
};

export default MapsProvider;